import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { averageCents, totalsByCurrency } from '../../platform/money/totals'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { toTxn } from './transactionWire'
import { TransactionsError } from './useTransactions'

/** Q12: at most 5 pages of 100. Beyond that the figures say "at least". */
export const STATS_PAGES = 5
const PAGE_SIZE = 100

export type CurrencyStats = { currency: string; visits: number; spentCents: number; averageCents: number }
export type MerchantStats = {
  /** Outflow rows counted, across currencies. */
  visits: number
  /** True when the 500-row window ended with more rows beyond it. */
  atLeast: boolean
  /** One entry per currency: amounts in different currencies are never summed (D21). */
  byCurrency: CurrencyStats[]
}

async function fetchStats(merchantId: string, year: number, signal: AbortSignal): Promise<MerchantStats> {
  const rows = []
  let atLeast = false
  // Sequentially, and stopping at the first page the server says is the last:
  // a merchant with 12 rows costs one request, not five.
  for (let page = 1; page <= STATS_PAGES; page++) {
    const { data, error, response } = await api.GET('/api/v1/transactions', {
      params: { query: { merchant_id: merchantId, start_date: `${year}-01-01`, page, page_size: PAGE_SIZE } },
      signal,
    })
    if (error !== undefined || data === undefined) throw new TransactionsError(error, response.status)
    rows.push(...data.transactions.map(toTxn))
    if (!data.has_more) break
    if (page === STATS_PAGES) atLeast = true
  }
  // Spending is the OUTFLOWS, read positive. Rows are signed for display (an expense is negative), so
  // an outflow is a negative row and its spend is the row negated. A visit is an outflow row, and the
  // average is spend per visit, so the three figures always describe the same rows.
  // Inflows at the merchant (a refund, or income from an employer) are NOT netted off: a refund is not
  // a negative visit, and netting it let income swamp the total (a merchant that pays you more than you
  // spent there would read as a negative "spent"). A merchant with only inflows has no visits, so it
  // falls to the empty state.
  const outflows = rows.filter((r) => r.amountCents < 0)
  const byCurrency = totalsByCurrency(
    outflows,
    (r) => -r.amountCents,
    (r) => r.currency,
  ).map(({ currency, cents }) => {
    const visits = outflows.filter((r) => r.currency === currency).length
    return { currency, visits, spentCents: cents, averageCents: averageCents(cents, visits) }
  })
  const visits = byCurrency.reduce((n, c) => n + c.visits, 0)
  return { visits, atLeast, byCurrency }
}

/**
 * `null` means the panel is hidden: the transaction has no merchant (Q12), and
 * no request is made. Otherwise the year-to-date figures for `merchantId`.
 */
export function useMerchantStats(merchantId: string | null, today: Date): AsyncState<MerchantStats> | null {
  const year = today.getFullYear()
  const query = useQuery({
    queryKey: keys.transactions.merchantStats(merchantId ?? '', year),
    queryFn: ({ signal }) => fetchStats(merchantId as string, year, signal),
    enabled: merchantId !== null,
  })
  const state = fromQuery(query, {
    message: () => "Couldn't load your history with this merchant.",
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (s) => s.visits === 0,
  })
  return merchantId === null ? null : state
}
